import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../App';
import { AuthContext } from '../../context/AuthContext';
import './Navbar.css';

// Logo SVG component that changes color based on theme
const LogoSVG: React.FC<{ isDarkTheme: boolean }> = ({ isDarkTheme }) => (
  <svg 
    id="Layer_1" 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 532.67 52.74"
    height="18"
    width="auto"
    className="logo-image"
  >
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m44.6,52.74v-27.69c0-6.02-3.06-8.98-9.19-8.98h-17.65v36.67H0V0h35.83C53.16,0,62.25,9.2,62.25,25.16v27.58h-17.65Z"
    />
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m105.2,52.74c-16.15,0-27.91-10.63-27.91-26.37S89.05,0,105.2,0h7.63c16.07,0,27.83,10.63,27.83,26.37s-11.77,26.37-27.83,26.37h-7.63Zm8.6-13.63c7.38,0,12.74-5.19,12.74-12.74s-5.35-12.82-12.74-12.82h-9.57c-7.38,0-12.82,5.19-12.82,12.82s5.35,12.74,12.82,12.74h9.57Z"
    />
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m157.1,52.74V0h30.35c16.07,0,27.83,10.63,27.83,26.37s-11.77,26.37-27.83,26.37h-30.35Zm31.32-13.63c7.39,0,12.66-5.19,12.66-12.74s-5.27-12.82-12.66-12.82h-17.12v25.56h17.12Z"
    />
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m230.57,52.74V0h50.63v13.55h-36.43v25.56h36.51v13.63h-50.71Zm13.81-20.85v-11.28h31.33l-.19,11.28h-31.14Z"
    />
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m297.62,52.74v-13.63h39.44c2.03,0,3.49-1.3,3.49-3.57s-1.46-3.65-3.49-3.65h-23.21c-9.82,0-16.88-6.49-16.88-15.9s7.06-15.98,16.88-15.98h40.08v13.55h-39.44c-2.03,0-3.41,1.22-3.41,3.49s1.38,3.57,3.41,3.57h23.21c9.9,0,16.96,6.57,16.96,15.98s-7.06,16.15-16.96,16.15h-40.08Z"
    />
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m424.8,52.74l-4.38-8.11-6-11.28-9.01-16.96-19.31,36.35h-15.66l26.94-48.85c1.46-2.6,3.57-3.89,6.33-3.89h3.24c2.76,0,4.87,1.3,6.33,3.89l26.94,48.85h-15.42Z"
    />
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m456.17,52.74V0h14.2v52.74h-14.2Z"
    />
    <path 
      fill={isDarkTheme ? "#fff" : "#4368b1"} 
      d="m486.26,52.74V0h14.28v39.11h32.13v13.63h-46.41Z"
    />
  </svg>
);

// Sun icon component
const SunIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"></circle>
    <line x1="12" y1="1" x2="12" y2="3"></line>
    <line x1="12" y1="21" x2="12" y2="23"></line>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
    <line x1="1" y1="12" x2="3" y2="12"></line>
    <line x1="21" y1="12" x2="23" y2="12"></line>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
  </svg>
);

// Moon icon component
const MoonIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

// Grid icon component
const GridIcon: React.FC = () => (
  <svg fill="currentColor" width="24" height="24" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <path d="M30 32h-10c-1.105 0-2-0.895-2-2v-10c0-1.105 0.895-2 2-2h10c1.105 0 2 0.895 2 2v10c0 1.105-0.895 2-2 2zM30 20h-10v10h10v-10zM30 14h-10c-1.105 0-2-0.896-2-2v-10c0-1.105 0.895-2 2-2h10c1.104 0 2 0.895 2 2v10c0 1.104-0.896 2-2 2zM30 2h-10v10h10v-10zM12 32h-10c-1.105 0-2-0.895-2-2v-10c0-1.105 0.895-2 2-2h10c1.104 0 2 0.895 2 2v10c0 1.105-0.896 2-2 2zM12 20h-10v10h10v-10zM12 14h-10c-1.105 0-2-0.896-2-2v-10c0-1.105 0.895-2 2-2h10c1.104 0 2 0.895 2 2v10c0 1.104-0.896 2-2 2zM12 2h-10v10h10v-10z"></path>
  </svg>
);

// Echo icon component
const EchoIcon: React.FC = () => (
  <svg 
    viewBox="0 0 363.57 363.57" 
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
  >
    <defs>
      <linearGradient id="blueNeonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4f46e5" />
        <stop offset="50%" stopColor="#3b82f6" />
        <stop offset="100%" stopColor="#60a5fa" />
      </linearGradient>
    </defs>
    <path 
      fill="url(#blueNeonGradient)"
      d="m195.76,10.66v342.25c0,4.7-4.01,8.7-9.54,10.1-1.41.36-2.89.55-4.44.55s-3.03-.19-4.44-.55c-5.54-1.4-9.54-5.4-9.54-10.1V10.66c0-5.9,6.26-10.66,13.98-10.66s13.98,4.77,13.98,10.66Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m251.69,65.4v232.78c0,5.88-6.26,10.65-13.98,10.65s-13.98-4.77-13.98-10.65V65.4c0-5.88,6.26-10.65,13.98-10.65s13.98,4.77,13.98,10.65Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m363.57,113.93v135.72c0,5.88-6.26,10.65-13.98,10.65s-13.98-4.77-13.98-10.65V113.93c0-5.88,6.26-10.65,13.98-10.65s13.98,4.77,13.98,10.65Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m363.57,140.05v83.47c0,5.88-6.26,10.65-13.98,10.65s-13.98-4.77-13.98-10.65v-83.47c0-5.88,6.26-10.65,13.98-10.65s13.98,4.77,13.98,10.65Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m307.63,140.05v83.47c0,5.88-6.26,10.65-13.98,10.65s-13.98-4.77-13.98-10.65v-83.47c0-5.88,6.26-10.65,13.98-10.65s13.98,4.77,13.98,10.65Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m139.83,65.4v232.78c0,5.88-6.26,10.65-13.98,10.65s-13.98-4.77-13.98-10.65V65.4c0-5.88,6.26-10.65,13.98-10.65s13.98,4.77,13.98,10.65Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m27.97,113.93v135.72c0,5.88-6.26,10.65-13.98,10.65S0,255.53,0,249.65V113.93C0,108.05,6.26,103.28,13.98,103.28s13.98,4.77,13.98,10.65Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m27.97,140.05v83.47c0,5.88-6.26,10.65-13.98,10.65s-13.98-4.77-13.98-10.65v-83.47c0-5.88,6.26-10.65,13.98-10.65s13.98,4.77,13.98,10.65Z"
    />
    <path 
      fill="url(#blueNeonGradient)"
      d="m83.9,140.05v83.47c0,5.88-6.26,10.65-13.98,10.65s-13.98-4.77-13.98-10.65v-83.47c0-5.88,6.26-10.65,13.98-10.65s13.98,4.77,13.98,10.65Z"
    />
  </svg>
);

function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, isAdmin } = useContext(AuthContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // URLs from environment variables
  const MAIN_NODESAIL_URL = process.env.REACT_APP_MAIN_NODESAIL_URL || "";
  const PAYMENT_SITE_URL = process.env.REACT_APP_PAYMENT_SITE_URL || "";
  const ECHO_SITE_URL = process.env.REACT_APP_ECHO_SITE_URL || "";
  const SEARCH_API_URL = process.env.REACT_APP_SEARCH_API_URL || "";

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <nav className={`navbar navbar-expand-lg ${theme === 'light' ? 'navbar-light bg-light' : 'navbar-dark bg-primary'} shadow-sm sticky-top`}>
      <div className="container">
        <Link className="navbar-brand d-flex align-items-center logo-container" to="/">
          <div className="logo-wrapper">
            <LogoSVG isDarkTheme={theme !== 'light'} />
            <span className="beta-text">support</span>
          </div>
        </Link>
        <div className="navbar-collapse" id="navbarNav">
          <ul className="navbar-nav me-auto">
            {/* Left navigation items can go here if needed */}
          </ul>
          
          {/* Right section with dashboard link, username, echo icon, theme toggle and grid icon */}
          <div className="ms-auto d-flex align-items-center">
            {isAuthenticated && (
              <Link to="/dashboard" className="dashboard-link me-3">
                Dashboard
              </Link>
            )}
            {isAuthenticated && isAdmin && (
              <Link to="/admin" className="dashboard-link me-3">
                Manager
              </Link>
            )}
            {isAuthenticated && user && (
              <div className="username-display me-3">
                <span className="user-name">{user.name}</span>
              </div>
            )}
            <a
              href={ECHO_SITE_URL}
              className="btn btn-link nav-link p-0 d-flex align-items-center echo-btn me-2"
              aria-label="Echo visualization"
              title="Echo"
              target="_blank"
              rel="noopener noreferrer"
            >
              <EchoIcon />
            </a>
            <button 
              onClick={toggleTheme}
              className="btn btn-link nav-link p-0 d-flex align-items-center theme-toggle"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              title="Switch Theme"
            >
              {theme === 'light' ? <MoonIcon /> : <SunIcon />}
            </button>
            <div className="dropdown ms-2" ref={dropdownRef}>
              <button
                className="btn btn-link nav-link p-0 d-flex align-items-center grid-toggle-btn"
                aria-label="Open apps menu"
                title="All Features"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                type="button"
                aria-expanded={dropdownOpen ? "true" : "false"}
              >
                <GridIcon />
              </button>
              {dropdownOpen && (
                <div className={`dropdown-menu dropdown-menu-end ${dropdownOpen ? 'show' : ''}`}>
                  <a 
                    href={MAIN_NODESAIL_URL} 
                    className="dropdown-item" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <span>Nodesail</span>
                    <small className="text-muted ms-auto">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="external-link-icon ms-2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </small>
                  </a>
                  <a 
                    href={PAYMENT_SITE_URL} 
                    className="dropdown-item" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <span>Nodesail Payment</span>
                    <small className="text-muted ms-auto">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="external-link-icon ms-2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </small>
                  </a>
                  <a 
                    href={ECHO_SITE_URL} 
                    className="dropdown-item" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <span>Nodesail Echo</span>
                    <small className="text-muted ms-auto">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="external-link-icon ms-2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </small>
                  </a>
                  <a 
                    href={SEARCH_API_URL} 
                    className="dropdown-item" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <span>Nodesail Search</span>
                    <small className="text-muted ms-auto">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="external-link-icon ms-2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </small>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;